/**
 * Survey API Service
 * Handles communication with WordPress REST API endpoint
 */

// Get API endpoint from environment variable or use default
const API_ENDPOINT = 'http://www.surradelupulo.com.br/wp-json/beer-survey/v1/submit';

/**
 * Submit survey data to WordPress API
 *
 * @param {Object} surveyData - Flattened survey answers (key:value pairs)
 * @returns {Promise<Object>} Response with success status and response_id
 * @throws {Error} If submission fails
 */
export async function submitSurvey(surveyData) {
  try {
    // Validate input
    if (!surveyData || typeof surveyData !== 'object') {
      throw new Error('Invalid survey data');
    }

    // Make API request
    const response = await fetch(API_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify(surveyData),
      // Don't include credentials for public endpoint
      // credentials: 'include', // Uncomment if using authentication
    });

    // Parse response
    const data = await response.json();

    // Handle different response codes
    if (!response.ok) {
      // Extract error message from WordPress error response
      const errorMessage = data.message ||
                          data.data?.message ||
                          `Server error: ${response.status}`;

      // Handle specific error codes
      switch (response.status) {
        case 400:
          throw new Error(`Validation error: ${errorMessage}`);
        case 429:
          throw new Error('Too many submissions. Please try again later.');
        case 500:
          throw new Error('Server error. Please try again.');
        default:
          throw new Error(errorMessage);
      }
    }

    // Return success data
    return {
      success: true,
      responseId: data.response_id,
      message: data.message || 'Survey submitted successfully!',
    };

  } catch (error) {
    // Log error for debugging
    console.error('Survey submission error:', error);

    // Re-throw with user-friendly message
    if (error.message.includes('Failed to fetch')) {
      throw new Error('Network error. Please check your internet connection.');
    }

    throw error;
  }
}

/**
 * Submit survey with retry logic
 * Automatically retries failed requests up to maxRetries times
 *
 * @param {Object} surveyData - Survey data to submit
 * @param {number} maxRetries - Maximum number of retry attempts (default: 2)
 * @returns {Promise<Object>} Response object
 */
export async function submitSurveyWithRetry(surveyData, maxRetries = 2) {
  let lastError;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      // Add delay between retries (exponential backoff)
      if (attempt > 0) {
        const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
        await new Promise(resolve => setTimeout(resolve, delay));
        console.log(`Retry attempt ${attempt}/${maxRetries}...`);
      }

      const result = await submitSurvey(surveyData);
      return result;

    } catch (error) {
      lastError = error;

      // Don't retry on validation errors or rate limits
      if (error.message.includes('Validation error') ||
          error.message.includes('Too many submissions')) {
        throw error;
      }

      // Continue to next retry attempt
      if (attempt < maxRetries) {
        console.warn(`Submission failed, retrying... (${attempt + 1}/${maxRetries})`);
      }
    }
  }

  // All retries failed
  throw new Error(`Failed after ${maxRetries + 1} attempts: ${lastError.message}`);
}

/**
 * Test API connectivity
 * Useful for debugging connection issues
 *
 * @returns {Promise<boolean>} True if API is reachable
 */
export async function testApiConnection() {
  try {
    // Try to reach the API endpoint with OPTIONS request
    const response = await fetch(API_ENDPOINT, {
      method: 'OPTIONS',
    });

    return response.ok || response.status === 405; // 405 is also OK (method not allowed but reachable)
  } catch (error) {
    console.error('API connection test failed:', error);
    return false;
  }
}

/**
 * Get API endpoint URL
 * Useful for debugging
 *
 * @returns {string} Current API endpoint URL
 */
export function getApiEndpoint() {
  return API_ENDPOINT;
}

/**
 * Validate survey data before submission
 * Basic client-side validation to catch common issues
 *
 * @param {Object} surveyData - Survey data to validate
 * @returns {Object} Validation result with isValid and errors
 */
export function validateSurveyData(surveyData) {
  const errors = [];

  // Check if data exists
  if (!surveyData || typeof surveyData !== 'object') {
    errors.push('Survey data is required');
    return { isValid: false, errors };
  }

  // Check if at least some data is provided
  const dataCount = Object.keys(surveyData).filter(
    key => surveyData[key] !== null && surveyData[key] !== undefined && surveyData[key] !== ''
  ).length;

  if (dataCount === 0) {
    errors.push('At least one field must be filled');
    return { isValid: false, errors };
  }

  // Validate age if provided
  if (surveyData.age !== undefined && surveyData.age !== null) {
    const age = parseInt(surveyData.age);
    if (isNaN(age) || age < 18 || age > 120) {
      errors.push('Age must be between 18 and 120');
    }
  }

  // Validate email if provided
  if (surveyData.email && surveyData.email.trim() !== '') {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(surveyData.email)) {
      errors.push('Invalid email format');
    }
  }

  // Validate rating fields (1-5)
  Object.keys(surveyData).forEach(key => {
    if (key.includes('_rating_')) {
      const rating = parseInt(surveyData[key]);
      if (!isNaN(rating) && (rating < 1 || rating > 5)) {
        errors.push(`Rating values must be between 1 and 5 (found ${rating} in ${key})`);
      }
    }
  });

  return {
    isValid: errors.length === 0,
    errors
  };
}

// Export API endpoint for configuration display
export { API_ENDPOINT };
